import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IATConfig, archivedConfig, SectionType, Study } from '../shared/types';
import { StateService } from './state.service';
import { ReloadingService } from './reloading.service';
import { LoadConfigService } from './load-config.service';
import { InstructionService } from './instruction.service';
import {
  ExtractedMediaFile,
  MediaLoaderService,
  MediaScope,
} from './media-loader.service';
import {
  instructionMapping,
  studySections,
} from '../constants/study-navigation.constants';
//import { utils } from 'protractor';
import { UtilsService } from './utils.service';

// TODO: need to skip certains instructions / reading modes if it was already done recently
@Injectable({
  providedIn: 'root',
})
export class StudyNavigationService {
  // private rawTimeline = ['introduction', 'questionnaire']
  //   .concat(this.loadConfigService.getRandomizedStudies())
  //   .concat(['questionnaire', 'final']);
  private questionnaireRepetitions: { [key: string]: number } = {
    q_before: 1,
    q_final: 1,
  };

  private clientConfig: Object;
  private timeline = [];
  private mediaFileTimeline: ExtractedMediaFile[] = [];

  private timelineIdx: number = 0;
  private studyTestId: number = 0;
  private questionnaireIdx: number = 0;
  // used to replace the placeholder for finished blocks (similar to timelineIdx but does not count certain sections as blocks)
  private interactiveSectionIdx: number = 0;
  private section: string;

  private currentIndex: number = 0;
  private blockNr: number = 0;
  private studyFinished: boolean = false;

  private mediaIdx: number = 0;

  private fallbackPracticeBrandName = 'Deloitte';
  private fallbackPracticeBrandImage = 'brand_practice.jpeg';
  private studies = this.loadConfigService.getRandomizedStudies();
  private lng = this.instructionService.getLanguageIdentifier();
  navigationSubject: BehaviorSubject<any>;
  mediaSubject: BehaviorSubject<ExtractedMediaFile>;

  IATconfig: IATConfig = {
    id: undefined,
    practiceMode: false,
    readingMode: false,
    blockName: 'persona',
    blockNr: 0,
    type: 'persona',
    mode: 'IAT',
    brandName: undefined,
    brandImage: undefined,
    brandPracticeImage: undefined,
    proceedAutomatically: true,
    stimuliPositions: [],
  };

  constructor(
    private stateService: StateService,
    private reloadingService: ReloadingService,
    private loadConfigService: LoadConfigService,
    private mediaLoader: MediaLoaderService,
    private instructionService: InstructionService,
    private utilsService: UtilsService
  ) {
    this.init(true);
  }

  /**
   * This function implicitly calls initRandomizedTests() and goes through all study configs of the cleaned study array and extracts the types
   * @returns an array of all study types in chronological order
   */
  private getTypeArray() {
    const typeArray = this.loadConfigService
      .getRandomizedStudies()
      .map((study) => study['type']);
    return typeArray;
  }

  private concatQuestionnaires(timeline: any[], repetition: number) {
    const arrQuestionnaire = Array(repetition).fill('questionnaire');
    return timeline.concat(arrQuestionnaire);
  }

  /**
   * This function replaces the initiated timeline of the studyNavigation class with an array of all study sections
   */
  initTimeline(
    typeArray: any[],
    questionnaireRepetitions: { [key: string]: number }
  ) {
    let timeline = [];

    timeline = timeline.concat(['introduction']);
    timeline = this.concatQuestionnaires(
      timeline,
      questionnaireRepetitions.q_before
    );
    timeline = timeline.concat(typeArray);
    timeline = this.concatQuestionnaires(
      timeline,
      questionnaireRepetitions.q_final
    );
    timeline = timeline.concat(['final']);

    this.timeline = timeline;
    //console.log('timeline in study nav: ', timeline)
    return timeline;
  }

  private addMediaToTimeline() {
    const mediaMapping =
      this.loadConfigService.getTestAmountToMediaBlockMapping();
    const relevantTestAmounts = Object.keys(mediaMapping);
    const modifiedTimeline = [];

    const isStudySection = (section) => {
      return [
        'persona',
        'brand',
        'modular',
        'IAT',
        'conjoint',
        'priming',
        'agreement',
      ].includes(section);
    };

    let amountSeenStudies = 0;

    for (let i = 0; i < this.timeline.length; i++) {
      const section = this.timeline[i];
      const isStudy = isStudySection(section);

      // only study sections have media files attached to them; hence, only need to look at study sections for modifying the timeline
      if (!isStudy) {
        modifiedTimeline.push(section);
      } else {
        const haveToAddBlockMedia = relevantTestAmounts.includes(
          amountSeenStudies.toString()
        );

        if (haveToAddBlockMedia) {
          const blockIdx = mediaMapping[amountSeenStudies.toString()];
          const blockMedia =
            this.loadConfigService.getBlockMediaByIdx(blockIdx);

          const mediaData = this.mediaLoader.extractMediaByPlaceholderAndScope(
            blockMedia.placeholder,
            MediaScope.BLOCK
          );

          // if (mediaData.name !== '') {
          this.mediaFileTimeline.push(mediaData);
          modifiedTimeline.push(mediaData.type);
          // }
        }

        const testMedia =
          this.loadConfigService.getRandomizedTestMediaByIdx(amountSeenStudies);
        const testHasMedia = typeof testMedia !== 'undefined';
        if (testHasMedia) {
          const testMediaData =
            this.mediaLoader.extractMediaByPlaceholderAndScope(
              testMedia.placeholder,
              MediaScope.TEST
            );

          // if (testMediaData.name !== '') {
          this.mediaFileTimeline.push(testMediaData);
          modifiedTimeline.push(testMediaData.type);
          // }
        }
        modifiedTimeline.push(section);

        amountSeenStudies++;
      }
    }

    return modifiedTimeline;
  }

  public async init(initSubject?: boolean) {
    this.clientConfig = this.loadConfigService.getClientConfig();
    // console.log('clientConfig in study nav init: ', this.clientConfig)
    this.questionnaireRepetitions =
      this.loadConfigService.getQuestionnairRepetitions();
    //has to be initialized after the clientConfig is loaded
    const typeArray = this.getTypeArray();
    this.initTimeline(typeArray, this.questionnaireRepetitions);
    this.timeline = this.addMediaToTimeline();

    this.section = this.timeline[this.timelineIdx];
    if (initSubject) this.initNavigationSubject();
    this.initMediaSubject();
    this.loadState(this.reloadingService.getState());
    // might be able to delete this call
    this.saveState(false);
    this.setCategoriesForFirstIAT(
      this.utilsService.filterConfigs(this.studies),
      this.lng
    );
    const nextInstruction = instructionMapping[this.section][this.currentIndex];

    // we do not want to call generateIATconfig as we already restored the config in loadState; another side effect is the increase in the block id, messing up the archiving of results and introducing inconsistencies

    // this is necessary in order to actually start at the correct location upon reload
    this.navigationSubject.next({
      section: this.section,
      title: nextInstruction,
      data: {
        questionnairePosition: this.getQuestionnaireIdx(),
        identifier: this.studyTestId,
      },
    });

    // this.stateService.setState(this.getStateConfig(), 'config');
  }

  /**
   * Initializes the navigation subject, based on section and title from the instructionMapping (e.g. IAT-IAT-test).
   * As the first section is never a questionnaire, the additional info in data should not be of importance for initialization.
   */
  private initNavigationSubject() {
    this.navigationSubject = new BehaviorSubject<{
      section: string;
      title: string;
      data?: { questionnairePosition: number; identifier?: number };
    }>({
      section: this.section,
      title: instructionMapping[this.section][this.currentIndex],
      data: {
        questionnairePosition: this.getQuestionnaireIdx(),
        identifier: null,
      },
    });
  }

  private initMediaSubject() {
    this.mediaSubject = new BehaviorSubject<ExtractedMediaFile>(
      this.mediaFileTimeline[this.mediaIdx]
    );
  }

  /**
   * This function is needed for IAT tests and priming tests. It replaces
   * this.IATconfig['stimuliPositions'] with an array of 4 elements
   * [left_category_1, left_category_2, right_category_1, right_category_2]
   * TODO: check what happens when neither IAT nor priming tests exist
   * @returns it exits the function when there were no IAT & priming tests found
   */
  setCategoriesForFirstIAT(IATconfigs: any[], lng: string) {
    if (IATconfigs.length === 0) {
      return;
    }
    // check whether addition of priming leads to wrong category mapping when having IAT AND priming tests in one study
    const firstIAT = IATconfigs ? IATconfigs[0] : undefined;

    if (firstIAT === undefined) return;

    if (firstIAT.hasOwnProperty('stimuliPositions')) {
      const mappedStimuliPositions = new Array(4).fill('');

      const definedStimuliPositions = firstIAT['stimuliPositions'][lng];

      let writeIdx = 0;
      for (const category of definedStimuliPositions.left) {
        if (writeIdx >= mappedStimuliPositions.length) break;

        mappedStimuliPositions[writeIdx] = category;
        writeIdx++;
      }

      writeIdx = 2;
      for (const category of definedStimuliPositions.right) {
        if (writeIdx >= mappedStimuliPositions.length) break;

        mappedStimuliPositions[writeIdx] = category;
        writeIdx++;
      }

      this.IATconfig['stimuliPositions'] = mappedStimuliPositions;
    }
  }

  /**
   * This function increases the currentIndex (if it exists) and calls the next navigationSubject
   */
  renderNext() {
    let persist = false;
    if (this.currentIndex < instructionMapping[this.section].length - 1) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0;

      this.setNextSection();
      persist = true;

      // TODO: remove after implementation of the IAT tests (or after they have been removed from client_config.json)
      this.skipIATTests(['OT', 'agreement']);
    }

    const nextInstruction = instructionMapping[this.section][this.currentIndex];

    this.generateIATconfig(nextInstruction);

    this.navigationSubject.next({
      section: this.section,
      title: nextInstruction,
      data: {
        questionnairePosition: this.getQuestionnaireIdx(),
        identifier: this.studyTestId,
      },
    });

    // set the new state only after generating the new config
    this.saveState(persist);
    window.scrollTo(0, 0);
  }

  private skipIATTests(IATTestArray) {
    while (IATTestArray.includes(this.section)) {
      this.setNextSection();
    }
  }

  /**
   * This function collects all info for the next site
   * @param nextInstruction string which indicates the next site (e.g. baseline-test)
   */
  private generateIATconfig(nextInstruction: string) {
    // baseline-test becomes ['baseline', 'test]
    const splitArr = nextInstruction.split('-');

    // gets current test config based on studyTestId
    const currentTestConfig = this.getCurrentTestConfig();

    if (typeof currentTestConfig === 'undefined') return;

    // this.IATconfig is updated, only if the string ends with "test"
    if (splitArr[splitArr.length - 1] === 'test') {
      let config = {
        blockNr: ++this.blockNr,
        blockName: `${this.section}`,
        type: this.section,
        mode: 'IAT',
        readingMode: false,
        practiceMode: false,
        brandName: undefined,
        brandImage: undefined,
        proceedAutomatically: true,
      };

      if (splitArr[0] === 'baseline') {
        config = {
          ...config,
          readingMode: true,
          blockName: `${this.section}-reading`,
          mode: 'reading',
        };
      } else if (splitArr[0] === 'practice') {
        config = {
          ...config,
          practiceMode: true,
          blockName: `${this.section}-practice`,
          mode: 'practice',
        };
      }

      if (this.section === 'brand') {
        if (config.practiceMode) {
          const practiceBrandName =
            this.loadConfigService.getPracticeBrandNameByStudyTestId(
              this.studyTestId
            );
          const practiceBrandImage =
            this.loadConfigService.getPracticeBrandImageByStudyTestId(
              this.studyTestId
            );

          config['brandName'] = practiceBrandName
            ? practiceBrandName
            : this.fallbackPracticeBrandName;
          config['brandImage'] = practiceBrandImage
            ? practiceBrandImage
            : this.fallbackPracticeBrandImage;
        } else {
          config['brandName'] =
            this.loadConfigService.getBrandNameByStudyTestId(this.studyTestId);
          config['brandImage'] =
            this.loadConfigService.getBrandImageByStudyTestId(this.studyTestId);
        }
      }

      if (this.section === 'IAT') {
        if (currentTestConfig.hasOwnProperty('stimuliPositions')) {
          const mappedStimuliPositions = new Array(4).fill('');

          const definedStimuliPositions =
            currentTestConfig['stimuliPositions'][
              this.instructionService.getLanguageIdentifier()
            ];

          let writeIdx = 0;
          for (const category of definedStimuliPositions.left) {
            if (writeIdx >= mappedStimuliPositions.length) break;

            mappedStimuliPositions[writeIdx] = category;
            writeIdx++;
          }

          writeIdx = 2;
          for (const category of definedStimuliPositions.right) {
            if (writeIdx >= mappedStimuliPositions.length) break;

            mappedStimuliPositions[writeIdx] = category;
            writeIdx++;
          }

          config['stimuliPositions'] = mappedStimuliPositions;
        } else {
          throw new Error('missing position for stimuli');
        }
      }

      if (
        this.loadConfigService.getProceedAutomaticallyFlag(this.studyTestId) !==
          undefined &&
        this.loadConfigService.getProceedAutomaticallyFlag(this.studyTestId) ===
          false
      ) {
        config['proceedAutomatically'] = false;
      }

      this.IATconfig = { ...config, id: currentTestConfig['id'] };
    }
  }

  getBrandImage(mode: string) {
    if (mode === 'practice') {
      const definedPracticeBrandName =
        this.loadConfigService.getPracticeBrandNameByStudyTestId(
          this.studyTestId
        );
      const definedPracticeBrandImage =
        this.loadConfigService.getPracticeBrandImageByStudyTestId(
          this.studyTestId
        );

      return {
        brandImage: definedPracticeBrandImage
          ? definedPracticeBrandImage
          : this.fallbackPracticeBrandImage,
        brandName: definedPracticeBrandName
          ? definedPracticeBrandName
          : this.fallbackPracticeBrandName,
      };
    } else {
      return {
        brandImage: this.loadConfigService.getBrandImageByStudyTestId(
          this.studyTestId
        ),
        brandName: this.loadConfigService.getBrandNameByStudyTestId(
          this.studyTestId
        ),
      };
    }
  }

  private skipEmptyQuestionnaire() {
    if (this.section !== 'questionnaire') return;

    while (
      this.section === 'questionnaire' &&
      this.loadConfigService.getQuestionnaireLength(
        this.lng,
        this.questionnaireIdx
      ) === 0
    ) {
      this.timelineIdx++;
      this.section = this.timeline[this.timelineIdx];
    }
  }

  private skipEmptyMedia() {
    if (this.section !== 'pdf') return;
    let currentMedia = this.mediaFileTimeline[this.mediaIdx];
    let fileName = currentMedia.name;

    while (this.section === 'pdf' && fileName === '') {
      this.timelineIdx++;
      this.section = this.timeline[this.timelineIdx];
      this.setNextMedia();
      currentMedia = this.mediaFileTimeline[this.mediaIdx];
      fileName = currentMedia.name;
    }
  }

  /**
   * Increments studyTestId if the current section is indicated as a study and returns boolean
   * value if section is study
   * @param section current section
   * @returns boolean
   */
  private incrementStudyTestId(section: string) {
    const isStudySection = studySections.includes(section);

    if (isStudySection) {
      this.studyTestId++;
    }

    return isStudySection;
  }

  /**
   * Increments questionnaireIdx if the current section is indicated as a questionnaire and returns
   * boolean value if section is questionnaire
   * @param section current section
   * @returns boolean
   */
  private incrementQuestionnaireIdx(section: string) {
    const isQuestionnaireSection = section === 'questionnaire';

    if (isQuestionnaireSection) {
      this.questionnaireIdx++;
    }

    return isQuestionnaireSection;
  }

  /**
   * If the current section is either a study or a questionnaire, the interactiveSectionIdx
   * is incremented
   * @param isStudySection indicates if section is study
   * @param isQuestionnaireSection indicates if section is questionnaire
   */
  private incrementInteractiveSectionIdx(
    isStudySection: boolean,
    isQuestionnaireSection: boolean
  ) {
    const isInteractiveSection = isStudySection || isQuestionnaireSection;

    if (isInteractiveSection) {
      this.interactiveSectionIdx++;
    }
  }

  /**
   * This function is called with renderNext() and increments:
   * studyTestId (if study), questionnaireIdx(if questionnaire),
   * interactiveSectionIdx (if either study or questionnaire), timelineIdx (always)
   */
  private setNextSection() {
    const isStudySection = this.incrementStudyTestId(this.section);
    const isQuestionnaireSection = this.incrementQuestionnaireIdx(this.section);
    this.incrementInteractiveSectionIdx(isStudySection, isQuestionnaireSection);

    this.timelineIdx++;
    this.section = this.timeline[this.timelineIdx];

    this.skipEmptyQuestionnaire();
    this.skipEmptyMedia();

    if (this.section === 'final') {
      this.studyFinished = true;
    }
  }

  getConfig() {
    return this.IATconfig;
  }

  getTestId() {
    return this.studyTestId;
  }

  getBlockId() {
    return this.blockNr;
  }

  incTestId() {
    if (
      this.studyTestId <
      this.loadConfigService.getRandomizedStudies().length - 1
    ) {
      this.studyTestId++;
    }
  }

  getCurrentPosition() {
    return {
      section: this.section,
      title: instructionMapping[this.section][this.currentIndex],
    };
  }

  getCurrentTestConfig() {
    return this.loadConfigService.getRandomizedTestConfigByIdx(
      this.studyTestId
    );
  }

  getAmountOfStudies() {
    const studies = this.loadConfigService.getRandomizedStudies();
    return studies.length;
  }

  private setConfig(config: IATConfig) {
    this.IATconfig = config;
  }

  getStateConfig(): archivedConfig {
    // need to also store mediaTimeline because generating it includes random elements which cannot be captured by simply restoring the random studyOrder
    return {
      config: this.IATconfig,
      section: this.section,
      idx: {
        studyTestId: this.studyTestId,
        currentSectionIdx: this.currentIndex,
        questionnaireIdx: this.questionnaireIdx,
        timelineIdx: this.timelineIdx,
        interactiveSectionIdx: this.interactiveSectionIdx,
        mediaTimelineIdx: this.mediaIdx,
      },
      studyOrder: this.loadConfigService.getRandomizedStudies(),
      mediaTimeline: this.mediaFileTimeline,
    };
  }

  // will only be called upon first load of the page (or reload)
  loadState(state) {
    if (!state) return;

    const config = state['config'];
    if (!config) return;

    const persistedIATConfig = config['config'];
    const persistedMediaTimeline = config['mediaTimeline'];
    const persistedStudyIdx = config['idx']['studyTestId'];
    const persistedSectionIdx = config['idx']['currentSectionIdx'];
    const persistedTimelineIdx = config['idx']['timelineIdx'];
    const persistedQuestionnaireIdx = config['idx']['questionnaireIdx'];
    const persistedInteractiveSectionIdx =
      config['idx']['interactiveSectionIdx'];
    const persistedMediaTimelineIdx = config['idx']['mediaTimelineIdx'];

    this.mediaFileTimeline = persistedMediaTimeline;
    this.currentIndex = persistedSectionIdx;
    this.studyTestId = persistedStudyIdx;
    this.timelineIdx = persistedTimelineIdx;
    this.questionnaireIdx = persistedQuestionnaireIdx;
    this.interactiveSectionIdx = persistedInteractiveSectionIdx;
    this.mediaIdx = persistedMediaTimelineIdx;

    this.setConfig(persistedIATConfig);

    // need to also set other parameters which are used for config generation
    this.blockNr = persistedIATConfig.blockNr;
    this.section = config['section'];
  }

  private saveState(persist: boolean) {
    this.stateService.setState(
      this.getStateConfig(),
      'config',
      persist,
      this.studyFinished
    );
  }

  getInteractiveSectionIdx() {
    return this.interactiveSectionIdx;
  }

  getQuestionnaireIdx() {
    return this.questionnaireIdx;
  }

  getTimelineIdx() {
    return this.timelineIdx;
  }

  getWasLastBlock() {
    return this.timelineIdx + 1 === this.timeline.length - 1;
  }

  getTimeline() {
    return this.timeline.slice();
  }

  getCurrentMedia() {
    return this.mediaFileTimeline[this.mediaIdx];
  }

  incMediaTimelineIdx() {
    this.mediaIdx++;
  }

  setNextMedia() {
    this.mediaSubject.next(this.mediaFileTimeline[++this.mediaIdx]);
  }
}
